'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import Image, { type StaticImageData } from 'next/image';
import { ReactNode } from 'react';
import { styled } from 'styled-components';

import { PostSummary } from '@/app/home-solar/blog/actions';
import { BlogSlideshow } from '@/app/home-solar/blog/components';
import { AsSeenIn, Eyebrow } from '@/app/home-solar/components';
import logoForbes from '@/app/home-solar/components/AsSeenIn/logo-forbes.svg';
import { ButtonLink, H2, H3, Section } from '@/app/lp/components';
import { TRACKING } from '@/helpers/tracking';

import bandBackyard from './band-backyard.webp';
import bandPhone from './band-phone.webp';
import logoCec from './california-energy-commission.webp';
import { FAQ_GROUPS, FaqEntry } from './faq-data';
import heroRooftops from './hero-rooftops.webp';
import logoNrel from './nrel.png';
import logoSpw from './solar-power-world.png';

const CONTENT_MAX_W = DS.BorealisTheme.screens.lg;

const GROUP_ICONS: Record<(typeof FAQ_GROUPS)[number]['id'], typeof DS.IconSun> = {
  'faq-aurora': DS.IconSun,
  'faq-basics': DS.IconDollarCircle,
  'faq-battery': DS.IconBatteryLightningVertical,
  'faq-incentives': DS.IconDiscount,
  'faq-install': DS.IconSolarPanel,
  'faq-installer': DS.IconCheckCircle,
  'faq-maintenance': DS.IconAlertShield,
  'faq-netmetering': DS.IconMeter,
  'faq-paying': DS.IconMoney,
  'faq-selling': DS.IconHouse,
};

type Props = { posts: PostSummary[] };

export const FaqPage = ({ posts }: Props) => (
  <article>
    <DS.DarkModeProvider>
      <x.section bg="black" mt={-60} px={{ _: 6, lg: 7 }}>
        <DS.Flex gap={7} maxW={CONTENT_MAX_W} mx="auto" pb={8} pt={10}>
          <DS.Flex alignItems="center" flexDirection={{ _: 'column', lg: 'row' }} gap={{ _: 8, lg: 10 }}>
            <DS.Flex flex={{ lg: '3 1 0' }} gap={5}>
              <Eyebrow color="white">Solar Basics</Eyebrow>
              <DS.Text as="h1" text={{ _: 'h2', lg: 'd3', md: 'd1' }}>
                The answers you&rsquo;re looking for
              </DS.Text>
              <DS.Text text="body16">
                Thinking about solar but drowning in conflicting advice? Here are clear, honest answers to the questions
                homeowners ask most, from the people whose technology the solar industry already trusts.
              </DS.Text>
              <GetEstimatesButton>Compare estimates from local installers</GetEstimatesButton>
            </DS.Flex>
            <x.div
              borderRadius={16}
              flex={{ lg: '2 1 0' }}
              h={{ _: 240, lg: 333, md: 320 }}
              maxW={{ lg: '484px' }}
              overflow="hidden"
              position="relative"
              w="100%"
            >
              <Image
                alt="Overhead aerial view of a neighborhood street with solar panels on several tile rooftops"
                fill
                priority
                sizes="(max-width: 1080px) 100vw, 484px"
                src={heroRooftops}
                style={{ objectFit: 'cover' }}
              />
            </x.div>
          </DS.Flex>
          <DS.Flex
            borderTop="1px solid"
            borderTopColor="uiDivider"
            flexDirection="row"
            flexWrap="wrap"
            gap={{ _: 5, md: 6 }}
            pt={6}
          >
            <Stat label="years in solar technology" value="13" />
            <Stat label="Solar companies use Aurora" value="7,000+" />
            <Stat label="Solar projects designed" value="20M+" />
            <Stat label="of top U.S. installers use Aurora" value="90%" />
          </DS.Flex>
        </DS.Flex>
      </x.section>
    </DS.DarkModeProvider>

    <Section maxW={CONTENT_MAX_W} pb={4} pt={8}>
      <DS.Flex flexDirection="row" flexWrap="wrap" gap={2} id="questions" scrollMarginTop="80px">
        {FAQ_GROUPS.map(({ id, navLabel }) => (
          <DS.Button as="a" bg="gray-200" borderColor="gray-200" href={`#${id}`} key={id} size="md" variant="secondary">
            {navLabel}
          </DS.Button>
        ))}
      </DS.Flex>
    </Section>

    <DS.Flex py={8}>
      {FAQ_GROUPS.slice(0, 1).map((group) => (
        <FaqGroupSection group={group} key={group.id} />
      ))}
    </DS.Flex>

    <Section
      bg="black"
      colorMode="dark"
      imageLeft={{ alt: 'Two people reviewing a 3D solar model of their home on a phone', height: 420, src: bandPhone }}
      maxW={CONTENT_MAX_W}
      pb={8}
      pt={8}
      px={0}
    >
      <H3>Estimates built for your specific home, not a general zip code.</H3>
      <p>
        Every estimate is modeled from your actual roof and electric bill, then priced by real local installers. Compare
        them on your own timeline, and only connect when you are ready.
      </p>
      <GetEstimatesButton>See estimates for your home</GetEstimatesButton>
    </Section>

    {FAQ_GROUPS.slice(1, 5).map((group) => (
      <FaqGroupSection group={group} key={group.id} />
    ))}

    <Section
      bg="black"
      colorMode="dark"
      imageRight={{
        alt: 'Couple relaxing in their backyard with a laptop, in front of their home with rooftop solar',
        height: 420,
        src: bandBackyard,
      }}
      maxW={CONTENT_MAX_W}
      pb={2}
      pt={2}
      px={0}
    >
      <H3>Going solar, step by step</H3>
      <p>
        From choosing an installer to the day your system turns on, here&rsquo;s every step: who does what, how long it
        takes, and what you&rsquo;ll decide along the way.
      </p>
      <ButtonLink
        event={{ name: TRACKING.BLOG_READ_MORE }}
        path="/home-solar/how-to-go-solar"
        size={{ _: 'sm', md: 'md' }}
        text="body16"
        variant="secondary"
      >
        See the full process
      </ButtonLink>
    </Section>

    {FAQ_GROUPS.slice(5).map((group) => (
      <FaqGroupSection group={group} key={group.id} />
    ))}

    <Section bg="uiBgAlternate" maxW={CONTENT_MAX_W}>
      <Eyebrow color="uiHelperCopy">Recognized across the industry</Eyebrow>
      <H3>Trusted by the people who build solar for a living</H3>
      <DS.Text color="uiHelperCopy" maxW="760px" text="body16">
        You may be meeting Aurora for the first time, but the solar industry has trusted our technology for over a
        decade. The same standard the pros rely on is the one we bring to helping you shop with confidence.
      </DS.Text>
      <DS.Flex flexDirection="row" flexWrap="wrap" gap={4}>
        <DS.Flex w={{ _: 'calc(50% - 8px)', lg: 'calc(25% - 12px)' }}>
          <RecognitionCard alt="Forbes" src={logoForbes}>
            Named to the Forbes AI 50, recognized as the only climate-tech company on the list the year it appeared.
          </RecognitionCard>
        </DS.Flex>
        <DS.Flex w={{ _: 'calc(50% - 8px)', lg: 'calc(25% - 12px)' }}>
          <RecognitionCard alt="Solar Power World" src={logoSpw}>
            Named best solar software by Solar Power World, an authority the industry reads.
          </RecognitionCard>
        </DS.Flex>
        <DS.Flex w={{ _: 'calc(50% - 8px)', lg: 'calc(25% - 12px)' }}>
          <RecognitionCard alt="NREL" src={logoNrel}>
            Performance and shade modeling independently validated by the U.S. National Renewable Energy Laboratory.
          </RecognitionCard>
        </DS.Flex>
        <DS.Flex w={{ _: 'calc(50% - 8px)', lg: 'calc(25% - 12px)' }}>
          <RecognitionCard alt="California Energy Commission" src={logoCec}>
            The first assessment tool approved by the California Energy Commission for building-code compliance.
          </RecognitionCard>
        </DS.Flex>
      </DS.Flex>
      <AsSeenIn />
    </Section>

    <Section maxW={CONTENT_MAX_W}>
      <DS.Flex alignItems="center" gap={6} textAlign="center">
        <H2 id="get-estimates">Ready to see what solar looks like for your home?</H2>
        <DS.Text color="uiHelperCopy" maxW={680} text="body20">
          Get free, personalized estimates and compare local installers, starting with just your address. No cost, no
          account, and no sales calls until you choose to connect.
        </DS.Text>
        <DS.Flex flexDirection={{ _: 'column', md: 'row' }} gap={3} w="fit-content">
          <GetEstimatesButton>Get free estimates</GetEstimatesButton>
          <DS.Button as="a" href="#questions" size="md" text="body16" variant="secondary">
            Back to top questions
          </DS.Button>
        </DS.Flex>
        <DS.Text pt={6} text="body12" textAlign="left">
          This page is for general education, not financial, tax, or legal advice. Estimates shown on Aurora Marketplace
          are modeled projections based on installer-supplied pricing applied to your property and energy profile; they
          are not binding quotes, and final design and pricing are confirmed by your chosen installer after a site
          assessment. Solar costs, savings, incentives, tax rules, and utility policies change over time and vary by
          location and situation. Confirm the details that apply to you before making a decision.
        </DS.Text>
      </DS.Flex>
    </Section>

    {posts.length > 0 ? (
      <BlogSlideshow
        posts={posts}
        subtitle="Guides and tips to help you go solar with confidence."
        title="From the blog"
      />
    ) : null}
  </article>
);

const Stat = ({ label, value }: { label: string; value: string }) => (
  <DS.Flex flex={{ _: '1 1 40%', md: '1 1 0' }} gap={1} minW={0}>
    <DS.Text fontWeight={300} text={{ _: 'h3', md: 'h2' }}>
      {value}
    </DS.Text>
    <DS.Text color="uiHelperCopy" text="body12">
      {label}
    </DS.Text>
  </DS.Flex>
);

const GetEstimatesButton = ({ children }: { children: ReactNode }) => (
  <ButtonLink
    event={{ name: TRACKING.LANDING_CLICK_GET_QUOTE }}
    path="/quote-builder"
    size={{ _: 'sm', md: 'md' }}
    text="body16"
    variant="cta"
  >
    {children}
  </ButtonLink>
);

const FaqDetails = styled.details`
  summary {
    list-style: none;
  }
  summary::-webkit-details-marker {
    display: none;
  }
  &[open] [data-expander] {
    transform: rotate(45deg);
  }
`;

const FaqItem = ({ a, q, related }: FaqEntry) => (
  <FaqDetails>
    <DS.Flex
      alignItems="flex-start"
      as="summary"
      cursor="pointer"
      flexDirection="row"
      gap={4}
      justifyContent="space-between"
      py={4}
    >
      <DS.Text as="span" fontWeight={500} text="body16">
        {q}
      </DS.Text>
      <x.div data-expander display="flex" flexShrink={0} transition="transform 0.2s ease">
        <DS.IconAdd color="uiHelperCopy" size={4} />
      </x.div>
    </DS.Flex>
    <DS.Flex gap={3} maxW="70ch" pb={6}>
      {a.map((paragraph) => (
        <DS.Text color="uiHelperCopy" key={paragraph.slice(0, 48)} text="body16">
          {paragraph}
        </DS.Text>
      ))}
      {related?.map(({ path, title }) => (
        <ButtonLink event={{ name: TRACKING.BLOG_READ_MORE }} key={path} path={path} size="sm" variant="tertiary">
          {title}
        </ButtonLink>
      ))}
    </DS.Flex>
  </FaqDetails>
);

const RecognitionCard = ({ alt, children, src }: { alt: string; children: ReactNode; src: StaticImageData }) => (
  <DS.Card borderRadius={0} boxShadow={1} gap={3} h="100%" p={5}>
    <x.div h="32px" position="relative" w="140px">
      <Image alt={alt} fill sizes="140px" src={src} style={{ objectFit: 'contain', objectPosition: 'left center' }} />
    </x.div>
    <DS.Text color="uiHelperCopy" text="body14">
      {children}
    </DS.Text>
  </DS.Card>
);

const FaqGroupSection = ({ group }: { group: (typeof FAQ_GROUPS)[number] }) => {
  const Icon = GROUP_ICONS[group.id];

  return (
    <Section maxW={CONTENT_MAX_W} pb={4} pt={8}>
      <DS.Flex alignItems="center" flexDirection="row" gap={3}>
        <Icon size={6} />
        <DS.Text as="h2" id={group.id} scrollMarginTop="80px" text="h1">
          {group.title}
        </DS.Text>
      </DS.Flex>
      <DS.Text color="uiHelperCopy" text="body16">
        {group.intro}
      </DS.Text>
      <DS.Flex>
        {group.items.map((item, index) => (
          <DS.Flex
            borderBottom={index === group.items.length - 1 ? '1px solid' : undefined}
            borderBottomColor="uiDivider"
            borderTop="1px solid"
            borderTopColor="uiDivider"
            key={item.q}
          >
            <FaqItem {...item} />
          </DS.Flex>
        ))}
      </DS.Flex>
    </Section>
  );
};
